import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../ui/Modal';
import { Button, Select } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import { api } from '../../lib/api';
import { differenceInHours } from 'date-fns';

export default function AiCallerModal({
  isOpen,
  onClose,
  lead,
  leadId,
  agencyId,
  thoughtlyCalls = [],
  onSuccess,
}) {
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const leadName = lead
    ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Lead'
    : 'Lead';

  const calledInLast24h = useMemo(() => {
    return thoughtlyCalls.some((c) => {
      const ts = c.created_at ? new Date(c.created_at).getTime() : 0;
      if (!ts) return false;
      return differenceInHours(new Date(), new Date(ts)) < 24;
    });
  }, [thoughtlyCalls]);

  useEffect(() => {
    if (!isOpen || !agencyId) return;
    setError('');
    setAgentsLoading(true);
    api.thoughtly
      .getAgents(agencyId)
      .then((rows) => {
        setAgents(rows);
        if (lead?.lead_type) {
          const match = rows.find((a) => a.lead_type === lead.lead_type);
          setSelectedAgentId(match?.id ?? rows[0]?.id ?? '');
        } else {
          setSelectedAgentId(rows[0]?.id ?? '');
        }
      })
      .catch((err) => setError(err.message || 'Could not load AI agents'))
      .finally(() => setAgentsLoading(false));
  }, [isOpen, agencyId, lead?.lead_type]);

  const handleConfirm = async () => {
    if (!selectedAgentId) {
      setError('Select an AI agent');
      return;
    }
    setSending(true);
    setError('');
    try {
      await api.thoughtly.triggerCall(leadId, selectedAgentId);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not start AI call');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !sending && onClose()}
      title="Send to AI Caller"
      className="max-w-md"
    >
      <div className="space-y-4 mt-2">
        <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3 text-sm">
          <p className="font-medium text-gray-900">{leadName}</p>
          <p className="text-gray-600 mt-0.5">{lead?.phone || '—'}</p>
        </div>

        {calledInLast24h && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            role="status"
          >
            This lead was already called by AI today
          </div>
        )}

        {agentsLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
            <Spinner className="text-primary" />
            Loading AI agents…
          </div>
        ) : agents.length === 0 ? (
          <p className="text-sm text-gray-600 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            No AI agents configured —{' '}
            <Link to="/settings/ai-caller" className="text-primary font-medium hover:underline">
              go to Settings
            </Link>{' '}
            to add one.
          </p>
        ) : (
          <Select
            label="AI Agent"
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            disabled={sending}
            options={[
              { value: '', label: '— Select agent —' },
              ...agents.map((a) => ({
                value: a.id,
                label: a.name,
              })),
            ]}
          />
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => void handleConfirm()}
            disabled={sending || agentsLoading || agents.length === 0 || !selectedAgentId}
          >
            {sending ? (
              <>
                <Spinner className="mr-2" />
                Sending…
              </>
            ) : (
              'Send Call'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
